/**
 * Layout the dashboards remember between visits: whether the activity rail
 * is collapsed (shared by Home and the client Overview, so hiding the rail in
 * one hides it in the other), and Home's view, layout, Mine / Team scope,
 * runway range and the By Type properties chosen in Display.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { HomeLayout } from "@/components/ui/layout-toggle";

export type HomeView = "briefing" | "dashboard" | "byType";
export type HomeScope = "mine" | "team";
export type RunwayDays = 30 | 120 | 365;
export type HomeProperty = "context" | "chips" | "date" | "assignee";
export type HomeProperties = Record<HomeProperty, boolean>;

export const DASHBOARD_LAYOUT_STORAGE_KEY = "dashboard.layout";

export const DEFAULT_HOME_PROPERTIES: HomeProperties = {
  context: true,
  chips: true,
  date: true,
  assignee: true
};

type State = {
  railCollapsed: boolean;
  homeView: HomeView;
  homeLayout: HomeLayout;
  homeScope: HomeScope;
  runwayDays: RunwayDays;
  homeProperties: HomeProperties;
};

type Actions = {
  setRailCollapsed: (collapsed: boolean) => void;
  toggleRail: () => void;
  setHomeView: (view: HomeView) => void;
  setHomeLayout: (layout: HomeLayout) => void;
  setHomeScope: (scope: HomeScope) => void;
  setRunwayDays: (days: RunwayDays) => void;
  setHomeProperty: (property: HomeProperty, shown: boolean) => void;
};

export const useDashboardLayout = create<State & Actions>()(
  persist(
    (set) => ({
      railCollapsed: false,
      homeView: "byType",
      homeLayout: "vertical",
      homeScope: "team",
      runwayDays: 120,
      homeProperties: DEFAULT_HOME_PROPERTIES,
      setRailCollapsed: (railCollapsed) => set({ railCollapsed }),
      toggleRail: () => set((s) => ({ railCollapsed: !s.railCollapsed })),
      setHomeView: (homeView) => set({ homeView }),
      setHomeLayout: (homeLayout) => set({ homeLayout }),
      setHomeScope: (homeScope) => set({ homeScope }),
      setRunwayDays: (runwayDays) => set({ runwayDays }),
      setHomeProperty: (property, shown) =>
        set((s) => ({
          homeProperties: { ...s.homeProperties, [property]: shown }
        }))
    }),
    { name: DASHBOARD_LAYOUT_STORAGE_KEY }
  )
);
